import React, { useState } from 'react';
import DataCard from '../common/DataCard';
import Loader from '../common/Loader';
import Pagination from '../common/Pagination';
import { MemberEndorsementDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import IconButton from '../common/IconButton';
import { MessageCircleWarning, Pen, Trash, Download, Search, FileText } from 'lucide-react';
import { EndorsementLicenceTypeConstants } from '../../constants/EndorsementLicenceTypeConstants';
import Badge from '../common/Badge';
import { getBadgeColors } from '../../utils/displayUtils';
import { renderCaliberBadge } from '../../utils/firearmDisplayUtils';
import Tippy from '@tippyjs/react';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { EndorsementStatusConstants } from '../../constants/EndorsementStatusConstants';
import { useFeedbackDrawer } from '../../context/FeedbackDrawerContext';
import { useMemberEndorsementApi } from '../../hooks/useMemberEndorsementApi';

interface MemberEndorsementDataCardProps {
    data: MemberEndorsementDTO[];
    handleEdit: (row: MemberEndorsementDTO) => void;
    handleDelete: (row: MemberEndorsementDTO) => void;
    handleGenerateCertificate: (row: MemberEndorsementDTO) => void;
    handleGenerateDeclaration: (row: MemberEndorsementDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;

    // Optional action renderers injected by the List page
    renderHeaderActions?: (row: MemberEndorsementDTO) => React.ReactNode;
    renderPrimaryActions?: (row: MemberEndorsementDTO) => React.ReactNode;
    renderSecondaryActions?: (row: MemberEndorsementDTO) => React.ReactNode;
}

const MemberEndorsementDataCard: React.FC<MemberEndorsementDataCardProps> = ({
    data,
    handleEdit,
    handleDelete,
    handleGenerateCertificate,
    handleGenerateDeclaration,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
    renderHeaderActions,
    renderPrimaryActions,
    renderSecondaryActions,
}) => {
    const { showFeedback } = useFeedbackDrawer();
    const { getFeedback } = useMemberEndorsementApi();
    const [generatingDeclarationId, setGeneratingDeclarationId] = useState<string | null>(null);

    const handleDeclarationClick = async (row: MemberEndorsementDTO) => {
        setGeneratingDeclarationId(row.id ?? null);
        try {
            await handleGenerateDeclaration(row);
        } finally {
            setGeneratingDeclarationId(null);
        }
    };

    return (
        <div>
            {isLoading ? (
                <div className="py-8 flex justify-center"><Loader /></div>
            ) : data.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">No records found</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {data.map((row) => {
                        const title = row.licenceType?.title || 'Unknown';

                        const leftBadge = (row: MemberEndorsementDTO) => {
                            const base = row.status?.colourGrading?.colour ?? '#ffffffff';
                            const { bg, text } = getBadgeColors(base, 0.18, 0.40);
                            const title = row.status?.title || 'Unknown';

                            return base ? (
                                <Badge
                                    label={title}
                                    variant="custom"
                                    customBgColor={bg}
                                    customTextColor={text}
                                />
                            ) : (
                                <span>{row.status?.title || 'Unknown'}</span>
                            );
                        };

                        const isGeneratingDeclaration = generatingDeclarationId === row.id;

                        return (
                            <DataCard<MemberEndorsementDTO>
                                key={row.id}
                                item={row}
                                collapsibleOnMobile={true}
                                leftBadgeNode={() => leftBadge(row)}
                                rightMeta={() => {
                                    return {
                                        label: 'Applied',
                                        value:
                                            <span>
                                                {row.createdUtc ? convertToUserTime(String(row.createdUtc), false) : 'n/a'}
                                            </span>,
                                    };
                                }}
                                title={() => title}
                                headerActions={renderHeaderActions}
                                fields={[
                                    { label: 'Type', render: (row) => row.firearm?.firearmType?.title ?? '-' },
                                    {
                                        label: 'Calibre',
                                        render: (row) => renderCaliberBadge(
                                            row.firearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.firearm?.caliberOther : row.firearm?.caliber?.title,
                                            row.firearm?.firearmSpecificId
                                        ) || <span>-</span>
                                    },
                                    { label: 'Make', render: (row) => row.firearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.firearm?.firearmMakeOther : row.firearm?.firearmMake?.title ?? '-' },
                                    { label: 'Frame / Receiver Serial Number', render: (row) => row.firearm?.frameSerial ?? '-' },
                                    { label: 'Barrel Serial Number', render: (row) => row.firearm?.barrelSerial ?? '-' },
                                ]}
                                files={
                                    [
                                        { hideIf: () => row.motivationFile1Id === undefined, label: 'Motivation File 1', id: row.motivationFile1Id ?? '' },
                                        { hideIf: () => row.motivationFile2Id === undefined, label: 'Motivation File 2', id: row.motivationFile2Id ?? '' },
                                        { hideIf: () => row.motivationFile3Id === undefined, label: 'Motivation File 3', id: row.motivationFile3Id ?? '' },
                                        { hideIf: () => row.motivationFile4Id === undefined, label: 'Motivation File 4', id: row.motivationFile4Id ?? '' },
                                    ]}
                                primaryActions={
                                    renderPrimaryActions ??
                                    ((row) => (
                                        <div className="flex justify-between w-full">
                                            <div className="flex gap-3">
                                                {row.status?.title?.includes('feedback') && (
                                                    <IconButton
                                                        label='Feedback'
                                                        color="blue"
                                                        icon={<MessageCircleWarning />}
                                                        onClick={() => showFeedback(() => getFeedback(row.id!))} />
                                                )}
                                                {row.status?.title?.includes('Approved') && (
                                                    <IconButton
                                                        label='Track Firearm License'
                                                        color="blue"
                                                        icon={<Search />}
                                                        onClick={() => {
                                                            window.open(
                                                                "https://www.saps.gov.za/services/firearm_status_enquiry.php?fid=8907195033087&fiserial=34534534",
                                                                "_blank"
                                                            );
                                                        }} />
                                                )}
                                                {row.licenceTypeId?.toLowerCase() === EndorsementLicenceTypeConstants.RENEWAL_OF_EXISTING_LICENCE.toLowerCase() && (
                                                    <IconButton
                                                        label='Declaration'
                                                        color="blue"
                                                        icon={isGeneratingDeclaration ? <Loader /> : <FileText />}
                                                        onClick={() => handleDeclarationClick(row)} />
                                                )}
                                            </div>
                                            <div className="flex gap-3">
                                                {(row.statusId === EndorsementStatusConstants.PENDING || row.statusId === EndorsementStatusConstants.PENDING_UPDATED || row.statusId === EndorsementStatusConstants.REVIEWED_WITH_FEEDBACK) && (
                                                    <>
                                                        <IconButton
                                                            label='Edit'
                                                            color="orange" icon={<Pen />}
                                                            onClick={() => handleEdit(row)} />
                                                        <IconButton
                                                            label='Delete'
                                                            color="red"
                                                            icon={<Trash />}
                                                            onClick={() => handleDelete(row)} />
                                                    </>
                                                )}

                                                {row.status?.title?.includes('Approved') && (
                                                    <IconButton
                                                        label='Download Certificate'
                                                        color="green"
                                                        icon={<Download />}
                                                        onClick={() => handleGenerateCertificate(row)} />

                                                )}

                                            </div>
                                        </div>
                                    ))
                                }
                                secondaryActions={renderSecondaryActions}
                            />
                        );
                    })}
                </div>
            )}

            {!isLoading && data.length > 0 && (
                <div className="mt-4">
                    <Pagination
                        currentPage={Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1}
                        totalPages={Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1}
                        onPageChange={setCurrentPage}
                        pageSize={Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12}
                        onPageSizeChange={setPageSize}
                    />
                </div>
            )}
        </div>
    );
};

export default MemberEndorsementDataCard;
